/**
 * client/src/components/org-switcher/options-list.tsx
 *
 * The switcher's dropdown panel: one row per profile the user can act as, with
 * the CURRENT one visibly marked (see option-row.tsx for why that matters).
 */

import type { OrgMembershipSummary } from "@/components/providers/org-context-provider";
import { HAIRLINE_RING, SectionEyebrow } from "@/components/premium";
// Glyphs come from the resolver's own kind→icon map, never re-imported from
// lucide here: two independent mappings would drift, and the trigger showing
// one glyph while the row for the same profile shows another is the exact
// defect class this whole feature exists to close.
import { PROFILE_KIND_ICON } from "@/lib/active-profile";
import { NavBadge } from "@/components/layout/nav-badge";
import { usePendingInvitationCounts } from "@/lib/pending-invitations";
import { CurrentMarker, optionClass } from "./option-row";

/** Static style object hoisted to module scope — avoids reallocating an
 *  identical `{ boxShadow }` object on every render (Reuse-First /
 *  efficiency, mirrors app-shell.tsx's SHELL_AMBIENT_WASH_STYLE pattern). */
const DROPDOWN_STYLE = { boxShadow: `${HAIRLINE_RING}, var(--elevation-2)` };

export interface ProfileOptionsListProps {
  readonly orgMemberships: readonly OrgMembershipSummary[];
  /**
   * `activeOrgId` + `onPlatform` are the ONLY selection inputs — every row's
   * "am I current?" is derived here from those two, uniformly. An earlier shape
   * took a pre-derived `personalSelected` boolean from the parent while still
   * recomputing the org and platform rows locally, which split one rule across
   * two components with three spellings.
   */
  readonly activeOrgId: string | null;
  readonly onPlatform: boolean;
  readonly isPlatformAdmin: boolean;
  readonly onPickPersonal: () => void;
  readonly onPickOrg: (orgId: string, orgName: string) => void;
  readonly onPickPlatform: () => void;
}

export function ProfileOptionsList({
  orgMemberships,
  activeOrgId,
  onPlatform,
  isPlatformAdmin,
  onPickPersonal,
  onPickOrg,
  onPickPlatform,
}: ProfileOptionsListProps) {
  const personalSelected = !onPlatform && activeOrgId === null;
  const pendingInvitations = usePendingInvitationCounts();
  return (
    <ul
      // DR-9 (#1582): long org lists (multi-org / platform admins) previously
      // grew past the viewport with no scroll — cap + scroll the list.
      // Premium surface treatment (W3 AC-10): rounded-2xl + hairline ring +
      // elevation-2 depth, layered over the existing theme-aware bg-bg-overlay
      // token — deliberately NOT the fixed-dark PANEL_GRADIENT, which would
      // break this theme-aware primitive in light mode (see PR return notes).
      className="rounded-2xl border border-border-default bg-bg-overlay max-h-[60dvh] overflow-y-auto"
      style={DROPDOWN_STYLE}
      role="listbox"
      aria-label="Switch profile"
    >
      {/* Names what the list is FOR — without it the list read as three
          unlabelled words. `aria-hidden` because the listbox's own aria-label
          already carries this string, and a `role="presentation"` <li> would
          otherwise leave a bare text node as a non-option child of a listbox. */}
      <li role="presentation" aria-hidden="true" className="px-3 pb-1 pt-2">
        <SectionEyebrow>Switch profile</SectionEyebrow>
      </li>
      <li>
        <button
          className={optionClass(personalSelected)}
          onClick={onPickPersonal}
          role="option"
          aria-selected={personalSelected}
        >
          <PROFILE_KIND_ICON.personal size={14} className="shrink-0 text-text-tertiary" aria-hidden="true" />
          <span>Personal</span>
          {/* Org-context discovery for a waiting invitation (#2171). The
              Invitations nav entry is personal-context only — the org list is
              already at the mobile tab ceiling — so someone working in an org
              would otherwise have no standing signal that an organisation is
              waiting on their answer. Badging the row they would switch
              through costs no nav item and points at the right place.
              Suppressed while Personal is already selected: the nav entry
              carries the same count there, and two chips for one fact reads as
              two things waiting. */}
          {!personalSelected && (
            <NavBadge count={pendingInvitations.total} label="Invitations" className="ml-auto" />
          )}
          {personalSelected && <CurrentMarker className="ml-auto" />}
        </button>
      </li>
      {orgMemberships.map((m) => {
        const selected = activeOrgId === m.orgId && !onPlatform;
        return (
          <li key={m.membershipId}>
            <button
              className={optionClass(selected)}
              onClick={() => onPickOrg(m.orgId, m.orgName)}
              role="option"
              aria-selected={selected}
            >
              <PROFILE_KIND_ICON.org size={14} className="shrink-0 text-text-tertiary" aria-hidden="true" />
              {/* Two lines, name first: the sidebar dropdown is only ~216px
                  wide, and on one line the Vetted + connection-type badges plus
                  the "Current" marker squeezed the ORG NAME — the single thing
                  the user is reading the row for — down to an ellipsis. */}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{m.orgName}</span>
                {/* The "Current" marker rides the METADATA line for org rows,
                    not the name line, for the same width reason. */}
                <span className="flex items-center gap-1.5 text-xs text-text-tertiary">
                  {selected && <CurrentMarker />}
                  {m.vettedAt && <span className="text-strenn-emerald">Vetted</span>}
                  <span>({m.connectionType})</span>
                </span>
              </span>
            </button>
          </li>
        );
      })}
      {isPlatformAdmin && (
        <>
          <li role="separator" aria-hidden className="border-t border-border-subtle my-1" />
          <li>
            <button
              className={optionClass(onPlatform)}
              onClick={onPickPlatform}
              role="option"
              aria-selected={onPlatform}
            >
              <PROFILE_KIND_ICON.platform size={14} className="shrink-0 text-text-tertiary" aria-hidden="true" />
              <span>Platform Console</span>
              {onPlatform && <CurrentMarker className="ml-auto" />}
            </button>
          </li>
        </>
      )}
    </ul>
  );
}
